/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input, Alert } from "@repo/ui";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { getWithdrawSteps } from "../../constants/steps";
import {
  createWithdrawRequest,
  getSuccessfulWithdrawalsCountByGiftTable,
  getTotalSuccessfulWithdrawalsByGiftTable,
} from "../../../../services/withdrawApiService";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { gsap } from "gsap";
import { getProduct, Product } from "../../../../services/productApiService";
import placeholderImage from "../../../../assets/placeholders/picture.webp";
import BackButton from "../../../../components/BackButton";
import { sumGiftsByGiftTableID } from "../../../../services/giftApiService";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";

const LOCALE = "modules_MyGiftTable_pages_WithdrawPage_WithdrawPageStep2";
const STEP1_CACHE_KEY = "withdrawStep1Cache";

const WithdrawPageStep2: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const steps = getWithdrawSteps(t);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stripeFlowEnabled =
    (searchParams.get("withdrawFlow") || "").toLowerCase() === "stripe";

  // Cash amount input state.
  const [amount, setAmount] = useState<string>("");
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  // The gift table products (from redux) include available quantity data.
  const giftTableProducts = useSelector(
    (state: RootState) => state.giftTable.products
  );
  const retentionFeeRate =
    useSelector((state: RootState) => state.giftTable.retentionFeeRate) || 0;
  const giftTableID = useSelector((state: RootState) => state.giftTable.ID);

  // Collected funds from the API.
  const [collectedSum, setCollectedSum] = useState<number>(0);
  // Total successful withdrawals.
  const [successfulWithdrawalsTotal, setSuccessfulWithdrawalsTotal] =
    useState<number>(0);
  // New product details state.
  const [productDetails, setProductDetails] = useState<Product[]>([]);
  // Instead of using a set of selected products, we now track a map: product id -> selected quantity.
  const [selectedQuantities, setSelectedQuantities] = useState<{
    [id: string]: number;
  }>({});
  const [, setWithdrawalCount] = useState(0);
  const [isLastWithdrawal, setIsLastWithdrawal] = useState(false);
  const [shouldBlink, setShouldBlink] = useState(false);

  // Calculate total value of selected products.
  const totalProductValue = productDetails.reduce((total, product) => {
    const qty = selectedQuantities[product.id] || 0;
    return total + product.price * qty;
  }, 0);

  // Compute the retention fee.
  const computedRetentionValue = collectedSum * retentionFeeRate;
  // Available funds: collected sum minus retention fee and already withdrawn funds.
  const availableFunds =
    collectedSum - computedRetentionValue - successfulWithdrawalsTotal;
  // Parse the cash amount entered by the user.
  const cashAmount = parseFloat(amount) || 0;
  // Total withdrawal amount is the sum of cash plus the value of selected products.
  const totalWithdrawalAmount = cashAmount + totalProductValue;

  // Trigger blink on totalWithdrawalAmount change.
  useEffect(() => {
    setShouldBlink(true);
    const timer = setTimeout(() => setShouldBlink(false), 1000);
    return () => clearTimeout(timer);
  }, [totalWithdrawalAmount]);

  // Load cached metrics from step1 to avoid duplicate calls.
  useEffect(() => {
    const loadCache = () => {
      try {
        const raw = localStorage.getItem(STEP1_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed.tableID !== giftTableID) return null;
        return parsed;
      } catch (e) {
        return null;
      }
    };

    const fetchData = async () => {
      if (!giftTableID) return;
      const cached = loadCache();
      if (cached) {
        setCollectedSum(cached.collectedSum || 0);
        setSuccessfulWithdrawalsTotal(cached.alreadyWithdrawn || 0);
        setWithdrawalCount(cached.withdrawalCount || 0);
        setIsLastWithdrawal((cached.withdrawalCount || 0) === 3);
        return;
      }

      try {
        const [collectedResult, withdrawnResult, countResult] = await Promise.all([
          sumGiftsByGiftTableID(giftTableID),
          getTotalSuccessfulWithdrawalsByGiftTable(giftTableID),
          getSuccessfulWithdrawalsCountByGiftTable(giftTableID),
        ]);
        setCollectedSum(collectedResult.sum);
        setSuccessfulWithdrawalsTotal(withdrawnResult.total);
        setWithdrawalCount(countResult.count);
        setIsLastWithdrawal(countResult.count === 3);
      } catch (error) {
        console.error("Error fetching data", error);
        setCollectedSum(0);
        setSuccessfulWithdrawalsTotal(0);
        setWithdrawalCount(0);
        setIsLastWithdrawal(false);
      }
    };

    fetchData();
  }, [giftTableID]);

  useEffect(() => {
    const fetchProducts = async () => {
      const fetchedProducts = await Promise.all(
        giftTableProducts
          .filter((p) => p.isPhysical)
          .map(async (p) => {
            try {
              return await getProduct(p.productID);
            } catch (error) {
              console.error("Error fetching product:", error);
              return null;
            }
          })
      );
      setProductDetails(fetchedProducts.filter(Boolean) as Product[]);
    };
    fetchProducts();
  }, [giftTableProducts]);

  // If this is the last withdrawal, preselect all physical products with maximum quantity.
  useEffect(() => {
    if (isLastWithdrawal) {
      const newQuantities: { [id: string]: number } = {};
      giftTableProducts
        .filter((p) => p.isPhysical)
        .forEach((p) => {
          // Use the full quantity from the gift table as the preselected amount.
          newQuantities[p.productID] = p.quantity;
        });
      setSelectedQuantities(newQuantities);
    }
  }, [isLastWithdrawal, giftTableProducts]);

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  // Helper to get the available pieces for a product (using the full quantity).
  const getAvailableForProduct = (productId: string): number => {
    const giftProd = giftTableProducts.find((p) => p.productID === productId);
    return giftProd ? giftProd.quantity : 0;
  };

  // Increment the selected quantity (up to the available pieces).
  const incrementProduct = (productId: string) => {
    const available = getAvailableForProduct(productId);
    setSelectedQuantities((prev) => {
      const current = prev[productId] || 0;
      if (current < available) {
        return { ...prev, [productId]: current + 1 };
      }
      return prev;
    });
  };

  // Decrement the selected quantity (not below 0).
  const decrementProduct = (productId: string) => {
    setSelectedQuantities((prev) => {
      const current = prev[productId] || 0;
      if (current > 0) {
        return { ...prev, [productId]: current - 1 };
      }
      return prev;
    });
  };

  const handleContinue = async () => {
    if (totalWithdrawalAmount <= 0) {
      setAlert({
        message: t("amountEmptyError"),
        level: "warn",
      });
      return;
    }
    if (totalWithdrawalAmount > availableFunds) {
      setAlert({
        message: t("insufficientFundsError"),
        level: "error",
      });
      return;
    }
    try {
      // Build productsToExchange as an array of objects with productId and quantity
      const productsToExchange = Object.entries(selectedQuantities)
        .filter(([, qty]) => qty > 0)
        .map(([prodId, qty]) => ({ productId: prodId, quantity: qty }));

      const withdrawRequest = {
        amount: totalWithdrawalAmount,
        currency: "MXN",
        giftTableID,
        productsToExchange, // Now using the new structure.
      };

      const response = await createWithdrawRequest(withdrawRequest);
      console.log("Withdrawal created successfully:", response);
      localStorage.setItem("withdrawal", JSON.stringify(response));
      const nextUrl = stripeFlowEnabled
        ? "/my-table/withdraw/step3?withdrawFlow=stripe"
        : "/my-table/withdraw/step3-manual";
      navigate(nextUrl);
    } catch (error: any) {
      console.error("Error creating withdrawal:", error);
    }
  };

  const handleBack = () => {
    navigate(`/my-table/withdraw/step1`);
  };

  return (
    <PageContainer>
      <div className="mt-16" />
      <StepIndicator steps={steps} currentStep={2} progressPercentage={50} />
      <AnimatedCard className="bg-white max-w-lg w-full">
        <BackButton text="Regresar" onClick={handleBack} />
        <h2 className="text-2xl font-bold mb-4 text-primary">{t("title")}</h2>
        <Alert
          message={t(
            "productExchangeInfo",
            "The final withdrawal amount is the sum of the cash amount you enter and the value of the products you select. If a product is unavailable, you'll receive its cash equivalent instead."
          )}
          level="info"
        />
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}
        {isLastWithdrawal && (
          <p className="text-red-500 mb-4">{t("lastWithdrawalNote")}</p>
        )}
        <div className="flex flex-col space-y-4">
          <p className="text-gray-600">
            {t("chooseAmount")}:{" "}
            <span className="font-bold">
              {availableFunds.toLocaleString("es-MX", {
                style: "currency",
                currency: "MXN",
              })}
            </span>
          </p>
          <Input
            placeholder={t("chooseAmount", "$ 0")}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          {productDetails.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold">{t("productListTitle")}</h3>
              <div className="space-y-4 min-h-[40vh] max-h-[40vh] overflow-y-auto mt-2">
                {productDetails.map((product) => {
                  const available = getAvailableForProduct(product.id);
                  const selectedQty = selectedQuantities[product.id] || 0;
                  return (
                    <div
                      key={product.id}
                      className="flex justify-between items-center p-4 border rounded-lg shadow-md bg-white"
                    >
                      <div className="flex items-center space-x-4">
                        <img
                          src={product.pictures?.[0]?.trim() || placeholderImage}
                          alt={product.title}
                          className="w-24 h-24 object-cover"
                        />
                        <div>
                          <h4 className="font-bold">{product.title}</h4>
                          <p className="text-sm">
                            {product.price.toLocaleString("es-MX", {
                              style: "currency",
                              currency: "MXN",
                            })}
                          </p>
                          <p className="text-xs text-gray-500">
                            {t("availablePieces")}: {available}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => decrementProduct(product.id)}
                          className="px-2 py-1 bg-gray-200 rounded"
                        >
                          –
                        </button>
                        <span>{selectedQty}</span>
                        <button
                          type="button"
                          onClick={() => incrementProduct(product.id)}
                          className="px-2 py-1 bg-gray-200 rounded"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Moved Total Withdrawal Display at the Bottom */}
          <p
            className={
              "text-gray-600 text-xl font-bold transition-colors duration-300" +
              (shouldBlink ? " highlight-blink" : "")
            }
          >
            {t("totalWithdrawal")}:{" "}
            <span>
              {totalWithdrawalAmount.toLocaleString("es-MX", {
                style: "currency",
                currency: "MXN",
              })}
            </span>
          </p>
          <Button
            label={t("continueButton")}
            onClick={handleContinue}
            type="primary"
            className="w-full mt-6"
          />
        </div>
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(WithdrawPageStep2));
